import type { AncillariesAddedAndRemovedEventProps } from '@/scopes/checkout/domain/analytics/types'
import {
  createServiceParticipantsMapper,
  type ServiceParticipantsMapper,
} from '@/scopes/checkout/infrastructure/api/model-mappers/serviceParticipantsMapper'
import { isNil } from 'ramda'
import {
  type CheckoutCarRentalService,
  type CheckoutCarRentalServiceOption,
  type ServiceMapper,
  ServiceType,
} from '../../../domain/types'
import {
  createServiceCarRentalOptionMapper,
  type ServiceCarRentalOptionMapper,
} from '../model-mappers/serviceCarRentalOptionMapper'
import {
  APIBookingDraft,
  APIBookingDraftCarRentalService,
  APIBookingDraftCarRentalServiceOption,
  APIBookingDraftRoom,
} from '../types'

interface Dependencies {
  serviceName: string
  serviceTypeMapper: string
  serviceParticipantsMapper?: ServiceParticipantsMapper
  serviceOptionMapper?: ServiceCarRentalOptionMapper
}

const MIN_AGE_DRIVER = 18
const RENTAL_CONDITIONS_MIN_AGE_DRIVER = 25
const RENTAL_CONDITIONS_MAX_AGE_DRIVER = 69

export const createCarRentalFilteringServiceMapper: (
  dependencies: Dependencies
) => ServiceMapper<CheckoutCarRentalService> = (dependencies) => {
  const {
    serviceName,
    serviceTypeMapper,
    serviceParticipantsMapper = createServiceParticipantsMapper(),
    serviceOptionMapper = createServiceCarRentalOptionMapper(),
  } = dependencies

  function getServiceName() {
    return serviceName
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper
  }

  function map(bookingDraft: APIBookingDraft) {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName) as APIBookingDraftCarRentalService

    if (!service)
      return null
    const { aName, options, usps } = service
    const carLists = mapCarLists(options, service) ?? []
    const rooms = mapRoomsForCars(options, bookingDraft.rooms)

    const anyCarSelected = carLists.some(({ selectedValue }) => selectedValue)

    const participants
      = bookingDraft?.participants.reduce(
        (acc, participant) => ({
          ...acc,
          [participant.id.toString()]: {
            ...serviceParticipantsMapper.mapParticipantWithAvailableOptions(participant, options),
            selectedValue: options
              .filter(({ isSelected }) => isSelected)
              .find(({ assignedParticipants }) => assignedParticipants.includes(participant.id))
              ?.uniqueId,
          },
        }),
        {},
      ) ?? {}

    return {
      id: aName,
      isSelected: anyCarSelected ?? !isNil(anyCarSelected),
      type: ServiceType.CarRental,
      usps: usps?.map(usp => ({ link: usp.link, text: usp.text })) ?? [],
      maxNumbersOfCarsToBeBooked: service.serviceBookingLimit ?? 1,
      isOpenJawPackage: service.isOpenJawPackage ?? false,
      includedCarOutOfStock: service.includedCarOutOfStock ?? false,
      includedCarPriceChanged: service.includedCarPriceChanged ?? false,
      carLists,
      options: options.reduce(
        (acc2: Record<string, CheckoutCarRentalServiceOption>, option) => ({
          ...acc2,
          [option.uniqueId ?? option.code]: serviceOptionMapper.map(option, service),
        }),
        {},
      ),
      participants,
      rooms,
      availableDrivers:
        bookingDraft?.participants
          ?.filter(participant => participant.ageCategory === 'Adult')
          .map(({ id }) => id.toString()) ?? [],
      configurations: {
        minAgeDriver: MIN_AGE_DRIVER,
        rentalConditionsMinAgeDriver: RENTAL_CONDITIONS_MIN_AGE_DRIVER,
        rentalConditionsMaxAgeDriver: RENTAL_CONDITIONS_MAX_AGE_DRIVER,
      },
    }
  }

  function mapCarLists(options: APIBookingDraftCarRentalServiceOption[], service: APIBookingDraftCarRentalService) {
    const selectedCars = options.filter(({ isSelected }) => isSelected)

    return selectedCars.map((selectedCar) => {
      const availableOptionsToUpgrade = options
        .filter(({ isSelected }) => !isSelected)
        .filter(({ code }) => code.split('??')[0] !== selectedCar.code.split('??')[0])
        .filter(({ uniqueId }) => uniqueId !== selectedCar.uniqueId)
        .map((option) => {
          return serviceOptionMapper.mapUpgradeOption(option, service, selectedCar.price)
        })

      return {
        selectedValue: selectedCar?.code,
        selectedId: selectedCar?.uniqueId ?? '',
        driverId: selectedCar?.driverId?.toString() ?? '',
        availableOptionsToUpgrade,
      }
    })
  }

  function mapRoomsForCars(
    options: APIBookingDraftCarRentalServiceOption[],
    rooms: APIBookingDraftRoom[],
  ) {
    const selectedOptions = options.filter(({ isSelected }) => isSelected)

    const sameParticipants = (a: number[], b: number[]) =>
      a.length === b.length && a.every(id => b.includes(id))

    return rooms.reduce<Record<string, {
      name: string
      id: string
      participantIds: string[]
      selectedValue?: string
    }>>((acc, room) => {
      const matchingOption = selectedOptions.find(({ assignedParticipants }) =>
        sameParticipants(assignedParticipants, room.participantIds),
      )

      acc[room.id.toString()] = {
        name: room.name,
        id: room.id.toString(),
        participantIds: room.participantIds.map(id => id.toString()),
        selectedValue: matchingOption?.uniqueId,
      }

      return acc
    }, {})
  }

  function mapSelection(service: CheckoutCarRentalService) {
    const { participants, availableDrivers, carLists } = service
    return (availableDrivers ?? []).map((id) => {
      const { birthdate } = participants[id]

      const currentValue = carLists?.find(({ driverId }) => driverId === id)?.selectedValue ?? null
      const currentId = carLists?.find(({ driverId }) => driverId === id)?.selectedId ?? null
      return {
        participantId: id,
        value: currentValue ?? null,
        id: currentId ?? null,
        driverBirthDate: birthdate,
        equipmentsWithQuantity: currentValue
          ? service.options[currentId ?? currentValue].selectedEquipmentsWithQuantity
          : [],
      }
    })
  }

  function mapAncillaryAddedAndRemovedToGA4Result(
    service: CheckoutCarRentalService,
    previousService: CheckoutCarRentalService,
  ): {
      ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[]
      ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[]
    } {
    const ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[] = []
    const ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[] = []

    let carsModified: { newCar: string | null, previousCar: string | null }[] = []

    if (service?.carLists?.length === 0 && previousService?.carLists?.length) {
      carsModified = previousService.carLists.reduce(
        (acc: { newCar: string | null, previousCar: string | null }[], currentCar, index) => {
          const newCar = service?.carLists ? service.carLists[index] : null

          if (!newCar) {
            acc.push({
              newCar: null,
              previousCar: currentCar?.selectedId || currentCar?.selectedValue || null, // Handle undefined selectedValue
            })
          }
          return acc
        },
        [],
      )
    }
    else {
      carsModified
        = service?.carLists?.reduce((acc: { newCar: string | null, previousCar: string | null }[], currentCar, index) => {
          const previousCar = previousService?.carLists ? previousService?.carLists[index] : null

          if (!previousCar) {
            acc.push({
              newCar: currentCar?.selectedId || currentCar.selectedValue,
              previousCar: null,
            })
            return acc
          }

          if (
            currentCar.selectedId !== previousCar.selectedId
            || currentCar.selectedValue !== previousCar.selectedValue
          ) {
            acc.push({
              previousCar: previousCar?.selectedId || previousCar.selectedValue,
              newCar: currentCar?.selectedId || currentCar.selectedValue,
            })
          }
          return acc
        }, []) ?? []
    }

    if (carsModified.length > 0) {
      carsModified.forEach((car) => {
        if (car.newCar) {
          const option = service.options[car.newCar]
          ancillaryAddMapped.push({
            id: car.newCar,
            name: option?.carClass || '',
            price: option?.price || 0,
            category: `Ancillary > Additional cost > ${car.newCar}`,
            quantity: 1,
            serviceType: car.newCar,
            extraProperties: {
              equipments: option?.equipments.filter(equipment => option?.selectedEquipments.includes(equipment.id)),
            },
          })
        }
        if (car.previousCar) {
          const option = previousService.options[car.previousCar]
          ancillaryRemoveMapped.push({
            id: car.previousCar,
            name: option?.carClass || '',
            price: option?.price || 0,
            category: `Ancillary > Additional cost > ${car.previousCar}`,
            quantity: 1,
            serviceType: car.previousCar,
            extraProperties: {
              equipments: option?.equipments.filter(equipment => option?.selectedEquipments.includes(equipment.id)),
            },
          })
        }
      })
    }

    return {
      ancillaryAddMapped,
      ancillaryRemoveMapped,
    }
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapAncillaryAddedAndRemovedToGA4Result,
  }
}
